import type {
  CodeRequest,
  CredentialRequest,
  SecondFactors,
  WebAuthnBeginResponse,
} from '@central/api';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { isApiError } from './errors';
import { ReauthPrompt } from './ReauthPrompt';

/** The user closed the re-authentication prompt; the guarded action did not run. */
export class ReauthCancelled extends Error {
  constructor() {
    super('re-authentication cancelled');
    this.name = 'ReauthCancelled';
  }
}

/**
 * The server asked for a fresh second factor and the user has none to prove:
 * no prompt can satisfy it.
 */
export class SecondFactorNeeded extends Error {
  constructor() {
    super(
      'This needs a second factor on your account. Set up an authenticator app or a passkey under Your sign-in, then try again.',
    );
    this.name = 'SecondFactorNeeded';
  }
}

/** Runs op, re-authenticating first when the server asks for a fresh second factor. */
export type Guard = <T>(op: () => Promise<T>) => Promise<T>;

/** An error worth showing: anything but a cancelled re-authentication. */
export function shownError(e: unknown): unknown {
  return e instanceof ReauthCancelled ? undefined : e;
}

/** The `/auth/reauth/*` calls of either realm (`consoleApi().auth`, `portalApi().auth`). */
export interface ReauthApi {
  reauthTotp(b: CodeRequest): Promise<unknown>;
  reauthWebAuthnBegin(): Promise<WebAuthnBeginResponse>;
  reauthWebAuthnFinish(b: CredentialRequest): Promise<unknown>;
}

export interface ReauthOptions {
  api: ReauthApi;
  /** The app's text for a failed re-authentication; defaults to the shared errorMessage. */
  describeError?: (e: unknown) => string;
  /** Mantine colour of the prompt's primary buttons. */
  buttonColor?: string;
}

/**
 * Guards actions that need a second factor proved in the last 10 minutes
 * (passkey removal, recovery codes, adding a factor, a staff recovery link).
 * `guard(op)` runs op; on 403 reauth_required it opens the prompt, and once
 * the user re-authenticates runs op again. Every guarded op that hits
 * reauth_required while the prompt is open waits on that same prompt, and
 * all of them retry (or reject with ReauthCancelled) when it settles. A user
 * with no TOTP and no passkey gets SecondFactorNeeded and no prompt. Render
 * `prompt` once in the component that calls guard.
 */
export function useReauth(
  factors: SecondFactors,
  opts: ReauthOptions,
): { guard: Guard; prompt: ReactNode } {
  const [open, setOpen] = useState(false);
  const pending = useRef<{
    done: Promise<void>;
    resolve: () => void;
    reject: (e: Error) => void;
  }>(undefined);

  const canProve = factors.totp || factors.passkeys > 0;
  const guard = useCallback(
    async <T,>(op: () => Promise<T>): Promise<T> => {
      try {
        return await op();
      } catch (e) {
        if (!isApiError(e, 'reauth_required')) throw e;
        if (!canProve) throw new SecondFactorNeeded();
        if (!pending.current) {
          let resolve!: () => void;
          let reject!: (e: Error) => void;
          const done = new Promise<void>((res, rej) => {
            resolve = res;
            reject = rej;
          });
          pending.current = { done, resolve, reject };
          setOpen(true);
        }
        await pending.current.done;
        return op();
      }
    },
    [canProve],
  );

  const settle = (ok: boolean) => {
    const p = pending.current;
    pending.current = undefined;
    setOpen(false);
    if (ok) p?.resolve();
    else p?.reject(new ReauthCancelled());
  };

  return {
    guard,
    prompt: (
      <ReauthPrompt
        opened={open}
        factors={factors}
        {...opts}
        onDone={() => settle(true)}
        onCancel={() => settle(false)}
      />
    ),
  };
}
